import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import Link from 'next/link';
import { hasPermission } from 'utils/permissions';
import useClickOutside from 'hooks/useClickOutside';
import clsx from 'clsx';
import {
  ChevronDown,
  User,
  Calendar,
  FileText,
  Receipt,
  Shield,
  LogOut,
} from 'lucide-react';

/**
 * Header user menu.
 *
 * Markup and behaviour are this app's own (useClickOutside + next/link soft
 * navigation); the styling is copied verbatim from the kit so it matches
 * `UserMenu` / `DropdownMenu` on kit.minthr.com:
 *  - trigger      → kit `UserMenu` trigger (TopHeader.md § UserMenu)
 *  - panel        → kit `DropdownMenu` content (`contentClasses`)
 *  - identity row → kit `UserMenu` identity header (a DropdownMenu `section`)
 *  - rows         → kit `DropdownMenu` item, md size (`itemBase` + `itemHeight`)
 *  - sign out     → kit `DropdownMenu` item, `variant="danger"` (`itemDanger`)
 *
 * Kit hover/highlight comes from Radix's `data-[highlighted]`; with plain
 * markup the equivalent is `hover:` + `focus-visible:`.
 */

/** kit DropdownMenu item — `itemBase` + `itemHeight.md`, hover/focus adapted. */
const ITEM_CLASSES =
  'tw-flex tw-items-center tw-gap-[10px] tw-h-[36px] tw-ps-[10px] tw-pe-[10px] ' +
  'tw-rounded-[4px] tw-text-[13px] tw-text-gray-800 tw-cursor-pointer tw-outline-none ' +
  'hover:tw-bg-gray-50 focus-visible:tw-bg-gray-50';

/** kit DropdownMenu separator. */
const SEPARATOR_CLASSES = 'tw-my-[4px] tw-h-px tw-bg-gray-100';

const UserDropdown: React.FC = () => {
  const [showdropdown, setshowdropdown] = useState(false);
  const dropRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { user, handleLogout } = useAuthContext();

  useClickOutside(dropRef, setshowdropdown, showdropdown);

  const closeDropdown = () => setshowdropdown(false);

  const photoUrl = `/upload/profileimages/${user.userId}/${user.picturePath}`;

  const menuItems = [
    {
      href: `/employee/${user.userId}/profile/public`,
      icon: <User size={14} strokeWidth={1.8} />,
      label: t('Profil.myprofile'),
    },
    {
      href: `/employee/${user.userId}/conge/calendar`,
      icon: <Calendar size={14} strokeWidth={1.8} />,
      label: t('holiday.myholidays'),
      permission: 'Conge.View',
    },
    {
      href: `/employee/${user.userId}/document/mydemand`,
      icon: <FileText size={14} strokeWidth={1.8} />,
      label: t('NavBar.MyDocument'),
      permission: 'DemandDocument.Profil.View.HimSelf',
    },
    {
      href: `/employee/${user.userId}/expenses/profile`,
      icon: <Receipt size={14} strokeWidth={1.8} />,
      label: t('My.Expenses'),
      permission: 'Profile.Expenses.View',
    },
    {
      href: `/employee/${user.userId}/usersettings/preference`,
      icon: <Shield size={14} strokeWidth={1.8} />,
      label: t('settings.link.security'),
      isSecurity: true,
    },
  ];

  return (
    <div className="tw-relative" ref={dropRef}>
      {/* Trigger — kit UserMenu, showIdentity={false} (avatar + chevron only;
          the tenant name lives in the header's logo slot). */}
      <button
        onClick={() => setshowdropdown(!showdropdown)}
        aria-haspopup="menu"
        aria-expanded={showdropdown}
        aria-label={user.fullname ?? t('Profil.myprofile')}
        className="tw-inline-flex tw-items-center tw-gap-[6px] tw-min-h-[32px] tw-py-[4px] tw-ps-[4px] tw-pe-[8px] tw-rounded-md tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-duration-100 hover:tw-bg-gray-50 focus-visible:tw-outline-none focus-visible:tw-ring-[2px] focus-visible:tw-ring-brand-50 focus-visible:tw-ring-offset-[1px]"
      >
        {/* kit Avatar, size sm (24px) */}
        <img
          src={photoUrl}
          alt=""
          className="tw-w-[24px] tw-h-[24px] tw-rounded-full tw-shrink-0 tw-overflow-hidden tw-object-cover tw-align-middle tw-bg-gray-100"
        />
        <ChevronDown size={12} strokeWidth={1.8} className="tw-text-gray-500" />
      </button>

      {showdropdown && (
        // Panel — kit DropdownMenu content. Width is shrink-to-fit between the
        // kit's min/max, not a fixed value.
        <div className="tw-absolute tw-top-full tw-end-0 tw-mt-[6px] tw-z-[80] tw-min-w-[180px] tw-max-w-[320px] tw-max-h-[400px] tw-overflow-auto tw-bg-white tw-border-[0.5px] tw-border-gray-200 tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] tw-p-[4px]">
          {/* Identity header — kit UserMenu identity block, rendered inside a
              DropdownMenu `section` label (hence the section paddings). */}
          <div className="tw-ps-[10px] tw-pe-[10px] tw-pt-[8px] tw-pb-[4px]">
            <div className="tw-flex tw-items-center tw-gap-[10px] tw-py-[4px]">
              <img
                src={photoUrl}
                alt=""
                className="tw-w-[24px] tw-h-[24px] tw-rounded-full tw-shrink-0 tw-overflow-hidden tw-object-cover tw-align-middle tw-bg-gray-100"
              />
              <div className="tw-min-w-0">
                <div className="tw-text-[13px] tw-font-medium tw-text-gray-800 tw-truncate tw-max-w-[160px] tw-leading-[1.3]">
                  {user.fullname}
                </div>
                {(user.fonction || user.role) && (
                  <div className="tw-text-[11px] tw-text-gray-500 tw-truncate tw-max-w-[160px] tw-leading-[1.3]">
                    {user.fonction || user.role}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={SEPARATOR_CLASSES} />

          {menuItems.map((item, index) => {
            if (item.permission && !hasPermission(item.permission)) return null;

            return (
              <React.Fragment key={index}>
                {/* Security sits in its own group, as in the kit user menu. */}
                {item.isSecurity && <div className={SEPARATOR_CLASSES} />}
                <Link
                  href={item.href}
                  onClick={closeDropdown}
                  className={clsx(ITEM_CLASSES, 'tw-no-underline')}
                >
                  <span className="tw-inline-flex tw-items-center tw-text-gray-500">
                    {item.icon}
                  </span>
                  <span className="tw-flex-1 tw-truncate">{item.label}</span>
                </Link>
              </React.Fragment>
            );
          })}

          <div className={SEPARATOR_CLASSES} />

          {/* Sign out — kit DropdownMenu item, variant="danger". */}
          <button
            onClick={() => handleLogout({ saveUrl: false })}
            className={clsx(
              ITEM_CLASSES,
              'tw-w-full tw-bg-transparent tw-border-0 tw-text-start',
              'tw-text-danger-700 hover:tw-bg-danger-50 hover:tw-text-danger-700 focus-visible:tw-bg-danger-50',
            )}
          >
            <span className="tw-inline-flex tw-items-center tw-text-danger-500">
              <LogOut size={14} strokeWidth={1.8} />
            </span>
            <span className="tw-flex-1 tw-truncate">{t('Signout')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
